import { ImagePaths } from '../../lib/imageUrls.js';

export const aiQuickStartSmallBusiness = {

    id: 61,
    slug: 'ai-quick-start-small-business',
    ogImage: '/og/courses/ai-quick-start-small-business.jpg',
    courseCode: 'AI-SMB-QS-001',
    title: 'AI Quick Start for Small Business Owners',
    subtitle: 'One evening of practical AI for quotes, customers, marketing, and admin',
    valueProposition: 'Use everyday AI tools to write quotes and proposals, handle customer emails and reviews, produce marketing copy, and clear the admin backlog, without spending money on tools you do not need.',
    duration: '4 hours',
    students: 'New',
    level: 'Foundational',
    image: ImagePaths.COURSE_4,
    shortDescription: 'AI-SMB-QS-001 — Practical AI for small business covering quotes and proposals, customer replies, marketing copy, admin, and business risk for Australian owners.',
    price: {
      aud: 395,
      currency: 'AUD'
    },
    stripeConfig: {
    priceId: 'price_1U3WMtKu1nyc322U8z7Vl1Om',
    productId: 'prod_V3deQjdhzM1f0c'
  },

    // Drives the cohort registration panel on the course page. No `cohort`
    // is set until the next run is dated, so the form and the confirmation
    // email say "the next cohort".
    // Presence of this field is what renders the form — it is deliberately
    // separate from `promo` so retiring the discount does not silently
    // remove the waitlist as well.
    waitlist: {
      label: 'Next cohort'
    },

    snapshot: {
      whoItsFor: [
        'Small business owners and sole traders doing their own quoting and admin',
        'Tradespeople and service businesses writing quotes and customer correspondence',
        'Retail, hospitality, and allied health operators managing reviews and marketing',
        'Consultants and freelancers producing proposals and client updates',
        'Practice managers and business partners running the back office of a small firm'
      ],
      accreditation: 'Grounded in the Privacy Act 1988 and the Australian Privacy Principles, Australian Consumer Law obligations on advertising and representations, and the ACSC Small Business Cyber Security Guide.'
    },

    logistics: {
      classSize: 'Maximum 20 participants so everyone works on their own business',
      requirements: [
        'Computer with a working microphone and internet access (minimum 10 Mbps)',
        'Access to at least one general AI assistant such as ChatGPT, Copilot, Claude, or Gemini',
        'A recent quote, proposal, or customer email from your own business to work on',
        'Basic familiarity with how your business quotes, invoices, and communicates with customers',
        'No technical, marketing, or prior AI experience required'
      ]
    },

    overview: 'Running a small business means writing the quote, chasing the invoice, answering the review, posting the ad, and updating the website, usually after hours and usually yourself. This four-hour evening course is about getting real, practical help with that work from AI tools you already have or can access for free. You will draft and refine a quote and a proposal from your own business, write customer emails including the difficult ones about price, delay, and complaint, respond to a negative online review without making it worse, produce marketing copy for a service page and a social post, and clear a chunk of routine admin such as reminder letters, supplier correspondence, and standard operating notes. There is a straight-talking segment on choosing tools when your software budget is small, because most owners are being sold subscriptions they do not need. The closing module covers where AI creates genuine business risk: customer data under the Privacy Act 1988, claims and representations under Australian Consumer Law, invoice and payment fraud, and the point at which a problem needs proper IT support, cyber security help, or a developer rather than another prompt. The price is 395 AUD including GST, in a single Thursday evening session.',

    outcomes: [
      'Draft and refine quotes and proposals that read professionally and state scope, exclusions, and terms clearly',
      'Write customer emails including price objections, delays, complaints, and payment follow-ups',
      'Respond to online reviews, both positive and negative, in a way that protects your reputation',
      'Produce marketing copy for a service page, a social post, and a customer newsletter from one brief',
      'Clear routine admin such as reminders, supplier correspondence, and standard operating notes faster',
      'Choose AI tools sensibly on a small budget and avoid paying for capability you already have',
      'Identify the customer and business information that must never enter a general AI tool',
      'Recognise the point at which an issue needs professional IT, cyber security, or development support'
    ],

    audience: [
      'Small business owners, operators, and sole traders',
      'Tradespeople and service businesses quoting and invoicing directly',
      'Retail, hospitality, and allied health operators',
      'Consultants, contractors, and freelancers writing proposals',
      'Practice managers and office administrators in small firms',
      'Franchisees and family business partners handling their own admin'
    ],

    prerequisites: [
      'You run, part-own, or administer a small business',
      'Comfortable using email, a web browser, and a word processor',
      'Access to a general AI assistant, or willingness to create a free account before the session',
      'No technical, marketing, or prior AI experience required',
      'Prompt Engineering Basics is helpful but not required'
    ],

    modules: [
      {
        title: 'Quotes, Proposals, and Winning the Job',
        duration: '1 hour',
        topics: [
          'A five-part prompt structure applied to a real quote from your own business, start to finish',
          'Writing scope, inclusions, exclusions, and assumptions so a quote does not become a dispute',
          'Building a proposal that explains the value rather than only listing the price',
          'Producing reusable quote and proposal templates you can adapt in minutes rather than hours',
          'Checking every claim you make against Australian Consumer Law before it goes to a customer'
        ]
      },
      {
        title: 'Customer Emails, Reviews, and Difficult Conversations',
        duration: '1 hour',
        topics: [
          'Drafting the hard emails: price objections, delays, scope changes, and payment follow-ups',
          'Responding to a negative online review calmly and publicly without escalating it',
          'Replying to positive reviews and enquiries at volume without sounding automated',
          'Setting a consistent business tone of voice and reusing it across every customer touchpoint',
          'Knowing which messages you must write personally, and why customers can tell'
        ]
      },
      {
        title: 'Marketing Copy, Admin, and Choosing Tools on a Small Budget',
        duration: '1 hour',
        topics: [
          'Producing a service page, a social post, and a customer newsletter from a single brief',
          'Writing product and service descriptions that are accurate rather than merely enthusiastic',
          'Clearing routine admin: reminder letters, supplier correspondence, and standard operating notes',
          'Invoicing and payment chasing sequences that stay polite and still get paid',
          'Choosing tools sensibly: free versus paid tiers, what you already have bundled, and the subscriptions not worth buying'
        ]
      },
      {
        title: 'Where AI Creates Business Risk, and When to Call for Help',
        duration: '1 hour',
        topics: [
          'The red-line list: customer personal and health information, payment and bank details, credentials, staff records, and confidential commercial terms',
          'Customer data and the Australian Privacy Principles under the Privacy Act 1988, in plain language for a small business',
          'Advertising claims, testimonials, and reviews under Australian Consumer Law, and how AI-written copy quietly creates exposure',
          'AI-assisted scams aimed at small business: invoice redirection, cloned voices, convincing phishing, and fake supplier emails, with the ACSC Small Business Cyber Security Guide as the baseline',
          'When to stop prompting and get professional help: IT support for systems and backups, cyber security for incidents and hardening, and a developer for anything customer-facing that handles data'
        ]
      }
    ],

    format: {
      type: 'Live online evening session, Thursday 6-10pm',
      includes: [
        '4 hours of live, instructor-led training in one evening',
        'Hands-on work on your own quotes, emails, and marketing copy',
        'A small business prompt library covering quoting, customer replies, reviews, and marketing',
        'Quote, proposal, and payment follow-up templates you can use immediately',
        'A one-page business risk card covering privacy, consumer law, and AI-assisted scams',
        'Certificate of completion',
        'Live Q&A throughout, with a dedicated segment on your own business'
      ]
    },

    nextIntake: {
      date: 'Register your interest — we will confirm the next cohort',
      details: 'Live online evening cohorts run on scheduled dates. Register your interest and we will confirm the next dates.'
    },

    // Real, dated sessions. The Enrol button is gated on there being a
    // future entry here — without one the page offers the waitlist instead,
    // because taking $395 for an unidentified session is not a supply
    // commitment under Australian Consumer Law. (#341)
    // Cohort opens the week of Monday 21 September 2026. NSW daylight saving
    // begins Sunday 4 October 2026 — see prompt-engineering-basics.js for why
    // the October run carries +11:00 rather than +10:00.
    sessions: [
      { start: '2026-09-24T18:00:00+10:00', end: '2026-09-24T22:00:00+10:00', capacity: 20 },
      { start: '2026-10-01T18:00:00+10:00', end: '2026-10-01T22:00:00+10:00', capacity: 20 },
      { start: '2026-10-08T18:00:00+11:00', end: '2026-10-08T22:00:00+11:00', capacity: 20 }
    ],

    pathways: [],

    skillsTaught: [
      'AI-assisted quoting and proposals',
      'Customer correspondence and review responses',
      'Small business marketing copy',
      'Admin and invoicing workflows',
      'Business risk and privacy awareness for AI use'
    ],

    hasCapstone: false,

    nextCourses: [
      'ai-for-business',
      'smb-cybersecurity-essentials'
    ],

    faqs: [
      {
        question: 'How is this different from AI Catalyst: Productivity and Policy for Business?',
        answer: 'AI Catalyst is a 24-hour program covering prompt engineering in depth, workflow automation, data privacy, and writing an organisational AI policy, and it suits businesses with staff and governance obligations. This four-hour course is the rung below it: one evening, your own quotes and customer emails, and no policy project. Owners often do this first to work out whether the longer course is worth the time.'
      },
      {
        question: 'What will it cost me in tools after the course?',
        answer: 'Potentially nothing. Everything taught works on the free tiers of the major assistants, and one module is specifically about not buying subscriptions you do not need. If you already pay for Microsoft 365 or Google Workspace, we cover what AI capability is already bundled in what you have.'
      },
      {
        question: 'Can I put customer details into AI to write a quote?',
        answer: 'Not real ones. Customer names, addresses, contact details, payment information, and anything health-related are personal information you hold obligations over under the Privacy Act 1988, and small businesses are increasingly covered as the exemptions narrow. The course shows you how to draft with placeholders and merge the real detail in your own system afterwards, which takes seconds and removes the exposure entirely.'
      },
      {
        question: 'Is AI-written marketing copy a legal risk?',
        answer: 'It can be. AI cheerfully writes superlatives, guarantees, comparisons, and outcome claims that would be misleading or deceptive conduct under Australian Consumer Law, and it will invent testimonials if you let it. The course covers the specific claim types that create exposure and gives you a short check to run over any copy before it is published.'
      },
      {
        question: 'Will this help me deal with scams and fake invoices?',
        answer: 'The final module covers it. AI has made phishing emails, fake supplier invoices, cloned voices, and payment redirection requests far more convincing, and small business is the primary target. We cover what these now look like, the simple verification habits that stop nearly all of them, and where the ACSC Small Business Cyber Security Guide sets the baseline you should be meeting.'
      },
      {
        question: 'What if I need more help than a course can give?',
        answer: 'The closing segment is honest about where training stops. Systems, backups, and device management need IT support. A live incident, a compromised mailbox, or hardening your business needs cyber security help. Anything customer-facing that stores data needs a developer rather than a prompt. We cover how to recognise which of the three you are looking at so you engage the right help the first time.'
      },
      {
        question: 'I am not technical at all. Will I keep up?',
        answer: 'Yes. There is no software to install, no configuration, and no jargon. If you can write an email and use a browser, you can do everything in this course. The class is capped at 20 so questions get answered as they come up rather than being deferred to the end.'
      },
      {
        question: 'Is the price inclusive of GST, and can I claim it?',
        answer: 'The price is 395 AUD including GST per person, which is the full amount payable, and a tax invoice is issued automatically on enrolment. Work-related training for your own business is generally deductible and the GST is generally claimable if you are registered, but check your own position with your accountant.'
      }
    ]
};
